import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    Users, 
    Calendar, 
    DollarSign, 
    TrendingUp,
    UserPlus,
    Heart,
    FileText,
    ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [appStats, setAppStats] = useState({ pending: 0 });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchStats();
        fetchAppStats();
        seedData();
    }, []);

    const seedData = async () => {
        try {
            await axios.post(`${API}/seed`);
        } catch (error) {
            // Ignore seed errors
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/dashboard/stats`);
            setStats(response.data);
        } catch (error) {
            toast.error('Failed to load dashboard stats');
        } finally {
            setLoading(false);
        }
    };

    const fetchAppStats = async () => {
        try {
            const response = await axios.get(`${API}/applications/stats/summary`);
            setAppStats(response.data);
        } catch (error) {
            console.error('Failed to load application stats');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading dashboard...</div>
            </div>
        );
    }

    const statCards = [
        {
            title: 'Total Members',
            value: stats?.total_members || 0,
            subtext: `${stats?.active_members || 0} active`,
            icon: Users,
            color: 'bg-[#C9A227]'
        },
        {
            title: 'Total Events',
            value: stats?.total_events || 0,
            subtext: `${stats?.upcoming_events || 0} upcoming`,
            icon: Calendar,
            color: 'bg-[#4A6B53]'
        },
        {
            title: 'Total Donations',
            value: stats?.total_donations || 0,
            subtext: `$${(stats?.donation_sum || 0).toLocaleString()}`,
            icon: DollarSign,
            color: 'bg-[#C9A227]'
        },
        {
            title: 'Engagement',
            value: stats?.active_members || 0,
            subtext: 'Active brothers',
            icon: TrendingUp,
            color: 'bg-[#4A6B53]'
        }
    ];

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
            {/* Page Header */}
            <div>
                <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                    Dashboard
                </h1>
                <p className="text-[#A0A0A0] mt-2">
                    Welcome to the Recovery Room admin portal
                </p>
            </div>

            {/* Pending Applications Alert */}
            {appStats.pending > 0 && (
                <Card className="border-[#C9A227]/50 bg-[#C9A227]/10 shadow-sm" data-testid="pending-apps-alert">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="bg-[#C9A227] p-2 rounded-lg text-[#3D4435]">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-semibold text-white">
                                        {appStats.pending} Pending Interest Form{appStats.pending > 1 ? 's' : ''}
                                    </p>
                                    <p className="text-sm text-[#A0A0A0]">New brothers are waiting to join the community</p>
                                </div>
                            </div>
                            <Button
                                onClick={() => navigate('/applications')}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                data-testid="review-apps-btn"
                            >
                                Review Now
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
                {statCards.map((stat, index) => (
                    <Card key={index} className="bg-[#4A5240] border-[#5A6350] shadow-sm card-hover" data-testid={`stat-card-${index}`}>
                        <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-[#A0A0A0] font-medium">{stat.title}</p>
                                    <p className="text-3xl font-bold text-white mt-2 font-['Playfair_Display']">
                                        {stat.value}
                                    </p>
                                    <p className="text-sm text-[#666] mt-1">{stat.subtext}</p>
                                </div>
                                <div className={`${stat.color} p-3 rounded-xl text-[#3D4435]`}>
                                    <stat.icon className="w-6 h-6" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Committee Stats */}
                <Card className="lg:col-span-7 bg-[#4A5240] border-[#5A6350] shadow-sm" data-testid="committee-stats-card">
                    <CardHeader>
                        <CardTitle className="text-xl font-semibold text-white font-['Playfair_Display']">
                            Committee Overview
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.committee_stats?.length > 0 ? (
                            <div className="space-y-4">
                                {stats.committee_stats.map((committee) => (
                                    <div key={committee.id} className="flex items-center justify-between p-4 bg-[#3D4435] rounded-lg border border-[#5A6350]">
                                        <div className="flex items-center gap-3">
                                            <div 
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: committee.color }}
                                            />
                                            <span className="font-medium text-white">{committee.name}</span>
                                        </div>
                                        <Badge 
                                            variant="secondary"
                                            className="bg-[#5A6350] text-[#A0A0A0]"
                                        >
                                            {committee.member_count} members
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-[#A0A0A0]">
                                <Heart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No committees yet. Add some to get started!</p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Members */}
                <Card className="lg:col-span-5 bg-[#4A5240] border-[#5A6350] shadow-sm" data-testid="recent-members-card">
                    <CardHeader>
                        <CardTitle className="text-xl font-semibold text-white font-['Playfair_Display']">
                            Recent Members
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {stats?.recent_members?.length > 0 ? (
                            <div className="space-y-3">
                                {stats.recent_members.map((member) => (
                                    <div key={member.id} className="flex items-center gap-3 p-3 hover:bg-[#3D4435] rounded-lg transition-colors">
                                        <div className="w-10 h-10 rounded-full bg-[#C9A227] flex items-center justify-center text-[#3D4435] font-medium">
                                            {member.name?.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-white truncate">{member.name}</p>
                                            <p className="text-sm text-[#666] truncate">{member.email}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-[#A0A0A0]">
                                <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No members yet. Add your first brother!</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Recent Donations */}
            <Card className="bg-[#4A5240] border-[#5A6350] shadow-sm" data-testid="recent-donations-card">
                <CardHeader>
                    <CardTitle className="text-xl font-semibold text-white font-['Playfair_Display']">
                        Recent Donations
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {stats?.recent_donations?.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[#5A6350]">
                                        <th className="text-left text-xs uppercase tracking-wider font-semibold text-[#A0A0A0] pb-3">Donor</th>
                                        <th className="text-left text-xs uppercase tracking-wider font-semibold text-[#A0A0A0] pb-3">Amount</th>
                                        <th className="text-left text-xs uppercase tracking-wider font-semibold text-[#A0A0A0] pb-3">Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.recent_donations.map((donation) => (
                                        <tr key={donation.id} className="border-b border-[#5A6350]">
                                            <td className="py-4 text-white font-medium">
                                                {donation.is_anonymous ? 'Anonymous' : donation.donor_name}
                                            </td>
                                            <td className="py-4 text-[#C9A227] font-semibold">
                                                ${donation.amount.toLocaleString()}
                                            </td>
                                            <td className="py-4 text-[#A0A0A0]">
                                                {new Date(donation.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-[#A0A0A0]">
                            <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No donations recorded yet</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DashboardPage;
